<template>
  <div class="recursive-menu-item">
    <!-- If item has children, render as expandable group -->
    <div
      v-if="hasChildren"
      class="menu-item-group"
    >
      <div
        class="menu-item-header"
        :class="{ 'is-expanded': isExpanded }"
        :data-testid="`sidebar-group-${itemSlug}`"
        @click="toggleExpanded"
      >
        <i v-if="item.icon" :class="item.icon" class="menu-icon"></i>
        <span class="menu-label">{{ t(item.label ?? '') }}</span>
        <i
          class="pi expand-icon"
          :class="isExpanded ? 'pi-chevron-down' : 'pi-chevron-right'"
        ></i>
      </div>

      <!-- Children container with transition -->
      <transition name="expand">
        <div v-if="isExpanded" class="menu-children">
          <RecursiveMenuItem
            v-for="child in item.children"
            :key="child.id || child.label"
            :item="child"
            :level="level + 1"
            @menuToggled="emit('menuToggled')"
          />
        </div>
      </transition>
    </div>

    <!-- If item has no children, render as clickable link -->
    <router-link
      v-else-if="item.route"
      :to="item.route"
      custom
      v-slot="{ href, navigate }"
    >
      <a
        :href="href"
        class="menu-item-link"
        :class="{ 'is-active': isActive }"
        :data-testid="`sidebar-link-${itemSlug}`"
        @click="(e) => handleLinkClick(e, navigate)"
      >
        <i v-if="item.icon" :class="item.icon" class="menu-icon"></i>
        <span class="menu-label">{{ t(item.label ?? '') }}</span>
      </a>
    </router-link>
    <div
      v-else
      class="menu-item-link"
      :data-testid="`sidebar-link-${itemSlug}`"
      @click="handleClick"
    >
      <i v-if="item.icon" :class="item.icon" class="menu-icon"></i>
      <span class="menu-label">{{ t(item.label ?? '') }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { log } from '../utils/DebugLogger';

// Get i18n composable
const { t } = useI18n();

import type { MenuItem } from '../router/routeMetadata';

// Props
const props = withDefaults(defineProps<{
  item: MenuItem;
  level?: number;
}>(), {
  level: 0,
});

// Composables
const route = useRoute();
const router = useRouter();

// Emits
const emit = defineEmits<{
  menuToggled: [];
}>();

// State
const isExpanded = ref<boolean>(false);

// Computed
const hasChildren = computed(() => {
  return props.item.children && props.item.children.length > 0;
});

const isActive = computed(() => {
  // Check if current route matches this menu item
  if (props.item.route && route.path === props.item.route) {
    return true;
  }
  return false;
});

const itemSlug = computed(() => {
  if (props.item.id) return props.item.id;
  return (props.item.label ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
});

// Methods
const toggleExpanded = (): void => {
  isExpanded.value = !isExpanded.value;
  // Emit event to parent so Sidebar can adjust page height
  emit('menuToggled');
};

const handleClick = (): void => {
  log('RecursiveMenuItem', 'handleClick', `Clicked on: ${props.item.label}`, { route: props.item.route });

  if (props.item.route) {
    router.push(props.item.route);
  }

  if (props.item.command && typeof props.item.command === 'function') {
    props.item.command();
  }
};

const handleLinkClick = (event: MouseEvent, navigate: (e?: MouseEvent) => void): void => {
  log('RecursiveMenuItem', 'handleLinkClick', `Clicked on: ${props.item.label}`, { route: props.item.route });

  // Let the browser handle modifier-key clicks (Ctrl/Cmd/Shift) and non-primary buttons
  // so users can open the link in a new tab/window via native browser behavior.
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
    return;
  }

  navigate(event);

  if (props.item.command && typeof props.item.command === 'function') {
    props.item.command();
  }
};
</script>

<style scoped>
.recursive-menu-item {
  width: 100%;
}

/* ============================================================================
   Group Header (Parent items with children)
   ============================================================================ */
.menu-item-group {
  margin-bottom: 4px;
}

.menu-item-header {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  color: white;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s ease;
  user-select: none;
}

.menu-item-header:hover {
  /* Use tenant primary color on hover */
  color: var(--app-primary-color);
  background: rgba(var(--app-primary-color-rgb), 0.1);
}

.menu-item-header.is-expanded {
  /* Use tenant primary color when expanded */
  color: var(--app-primary-color);
}

/* ============================================================================
   Leaf Items (Clickable links)
   ============================================================================ */
.menu-item-link {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  color: #d0d0d0; /* Light grey text */
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.2s ease;
  user-select: none;
}

.menu-item-link:hover {
  color: #ffffff;
  /* Use tenant primary color for hover background */
  background: rgba(var(--app-primary-color-rgb), 0.1);
}

.menu-item-link.is-active {
  /* Use tenant primary color for active state */
  color: var(--app-primary-color);
  background: rgba(var(--app-primary-color-rgb), 0.15);
  font-weight: 600;
}

/* ============================================================================
   Icons and Labels
   ============================================================================ */
.menu-icon {
  /* Use tenant primary color for icons */
  color: var(--app-primary-color);
  font-size: 15px;
  margin-right: 10px;
  flex-shrink: 0;
}

.menu-label {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.expand-icon {
  margin-left: auto;
  font-size: 12px;
  transition: transform 0.2s ease;
  color: #a0a0a0; /* Light grey */
}

/* ============================================================================
   Children Container (Nested items)
   ============================================================================ */
.menu-children {
  padding-left: 20px;
  margin-top: 4px;
  overflow: hidden;
}

/* Nested level styling - indent and dim colors slightly */
.recursive-menu-item .recursive-menu-item .menu-item-link {
  color: #b0b0b0; /* Slightly dimmer grey for nested items */
  font-size: 12px;
}

.recursive-menu-item .recursive-menu-item .menu-item-link:hover {
  color: #ffffff;
}

/* Third level and beyond - further indent */
.recursive-menu-item .recursive-menu-item .recursive-menu-item .menu-item-link {
  font-size: 11px;
  padding-left: 16px;
}

/* ============================================================================
   Expand/Collapse Animation
   ============================================================================ */
.expand-enter-active,
.expand-leave-active {
  transition: all 0.3s ease;
  max-height: 1000px;
  opacity: 1;
}

.expand-enter-from,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
  margin-top: 0;
}

/* ============================================================================
   Responsive Adjustments
   ============================================================================ */
@media (max-width: 768px) {
  .menu-item-header,
  .menu-item-link {
    font-size: 14px;
    padding: 12px 14px;
  }

  .menu-icon {
    font-size: 16px;
  }
}
</style>
